import { useCallback, useEffect, useMemo, useState } from 'react';
import { readCache, writeCache, pruneCacheGroup } from '../../../services/localCache';
import { Plus, Trash2, Wallet, CalendarClock, ChevronDown } from 'lucide-react';
import { getExpensesRange, deleteExpense, ExpensesResp, Expense, FinSection, moneyEntero as money } from '../api';
import ExpenseForm from './ExpenseForm';
import RecurringPanel from './RecurringPanel';
import CatalogoModal from './CatalogoModal';
import { useCatalogo } from './ConceptoSelect';
import EmptyState from '../../../components/EmptyState';
import CountUp from '../../../components/CountUp';
import { Skeleton } from '../../../components/Skeleton';
import { useAuth } from '../../../context/AuthContext';
import { todayLocal, addMonthsClamped } from '../../../utils/dates';
import { useToast } from '../../../context/ToastContext';

type Range = 'day' | 'week' | 'month';

function fmtDay(dt: string) {
  const d = new Date(dt);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}
function fromISO(d: string) { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd); }
function toISO(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
/** Avanza el ancla por la unidad del rango. */
function stepAnchor(anchor: string, range: Range, delta: number) {
  if (range === 'month') return addMonthsClamped(anchor, delta); // sin saltos en días 29-31
  const d = fromISO(anchor);
  d.setDate(d.getDate() + (range === 'day' ? delta : delta * 7));
  return toISO(d);
}
/** Etiqueta legible del periodo a partir de from/to del backend (solo la 1.ª letra en mayúscula:
 *  con la clase `capitalize` salía «Septiembre De 2026»). */
function periodLabel(resp: ExpensesResp | null, range: Range) {
  const t = periodLabelRaw(resp, range);
  return t.charAt(0).toUpperCase() + t.slice(1);
}
function periodLabelRaw(resp: ExpensesResp | null, range: Range) {
  if (!resp?.from) return '—';
  const f = fromISO(resp.from);
  if (range === 'day') return f.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  if (range === 'month') return f.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  const last = fromISO(resp.to || resp.from); last.setDate(last.getDate() - 1);
  return `${f.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} – ${last.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`;
}

/**
 * Lista de gastos de UN apartado (Mabel 7-oct-2026: «en la barrita ocupo que vayan los apartados»).
 * - Gastos      → secciones ['egreso'] (+ lo viejo sin clasificar). La nómina y los bonos ya NO salen aquí:
 *                 viven en la pestaña Nómina (siguen contando en el resultado del mes, eso es del reporte).
 * - Inversiones → ['inversion'].
 * - Nómina › «Otros gastos de personal» → ['nomina'] sin los conceptos que crea la nómina sola (`excluir`).
 * El backend devuelve todo el periodo; el filtro y los totales se hacen aquí.
 */
export default function ExpensesPanel({ secciones = ['egreso'], excluir = [], titulo = 'Total de egresos', textoBoton = 'Registrar gasto',
  conCatalogo = true, conProgramados = false, vacio }: {
  secciones?: FinSection[]; excluir?: string[]; titulo?: string; textoBoton?: string;
  conCatalogo?: boolean; conProgramados?: boolean; vacio?: string;
}) {
  const { user } = useAuth();
  const { catalogo } = useCatalogo();
  const [verProgramados, setVerProgramados] = useState(false);
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  const [range, setRange] = useState<Range>('month');
  const [anchor, setAnchor] = useState<string>(todayLocal());
  const [resp, setResp] = useState<ExpensesResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCatalogo, setShowCatalogo] = useState(false);   // catálogo de Mabel (7-oct-2026)

  const reload = useCallback(() => {
    // SWR fechado: pinta lo último conocido de este rango/ancla y refresca por detrás
    const key = `fin-exp:${range}:${anchor}`;
    const cached = readCache<ExpensesResp>(key);
    if (cached) { setResp(cached); setLoading(false); } else setLoading(true);
    getExpensesRange(range, anchor)
      .then((r) => { setResp(r); writeCache(key, r); pruneCacheGroup('fin-exp', key); })
      .finally(() => setLoading(false));
  }, [range, anchor]);

  useEffect(() => { reload(); }, [reload]);

  const onCreated = useCallback((m: string) => {
    setShowForm(false);
    setRange('month'); setAnchor(`${m}-01`); // muestra el mes del gasto recién creado
  }, []);

  async function onDelete(id: number) {
    if (!confirm('¿Eliminar este gasto? Esta acción no se puede revertir.')) return;
    try {
      await deleteExpense(id);
      toast.success('Gasto eliminado');
      reload();
    } catch (e: any) { toast.error(e?.message || 'No se pudo eliminar'); }
  }

  const navRange = (delta: number) => setAnchor((a) => stepAnchor(a, range, delta));

  // Conceptos a esconder (por su system_key del catálogo: los que escribe la nómina sola).
  const ocultos = useMemo(() => {
    const ids = new Set<number>();
    for (const s of catalogo || []) for (const c of s.categorias) for (const k of c.conceptos) if (k.system_key && excluir.includes(k.system_key)) ids.add(k.concept_id);
    return ids;
  }, [catalogo, excluir]);
  const rows: Expense[] = useMemo(() => (resp?.data || []).filter((x) => {
    if (x.concept_id && ocultos.has(x.concept_id)) return false;
    if (!x.concept_section) return secciones.includes('egreso');   // viejos sin concepto → Gastos
    return secciones.includes(x.concept_section as FinSection);
  }), [resp, ocultos, secciones]);
  const total = useMemo(() => Math.round(rows.reduce((a, x) => a + (Number(x.amount) || 0), 0) * 100) / 100, [rows]);
  // Desglose Categoría › Concepto del periodo.
  const porCategoria = useMemo(() => {
    const m = new Map<string, { nombre: string; total: number; n: number; conceptos: Map<string, { total: number; n: number }> }>();
    for (const x of rows) {
      const cat = x.concept_category || x.category || 'Sin categoría';
      const con = x.concept_name || '';
      const g = m.get(cat) || { nombre: cat, total: 0, n: 0, conceptos: new Map() };
      g.total += Number(x.amount) || 0; g.n += 1;
      if (con) { const k = g.conceptos.get(con) || { total: 0, n: 0 }; k.total += Number(x.amount) || 0; k.n += 1; g.conceptos.set(con, k); }
      m.set(cat, g);
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  }, [rows]);
  const catMax = Math.max(1, ...porCategoria.map((c) => c.total));
  // Fijos vs variables (solo si la API ya trae `is_fixed`; los sin marcar no suman a ninguno).
  const fijos = useMemo(() => {
    if (!rows.some((x) => x.is_fixed !== undefined)) return null;
    const f = rows.filter((x) => Number(x.is_fixed) === 1).reduce((a, x) => a + (Number(x.amount) || 0), 0);
    const v = rows.filter((x) => x.is_fixed != null && Number(x.is_fixed) === 0).reduce((a, x) => a + (Number(x.amount) || 0), 0);
    return f || v ? { f, v } : null;
  }, [rows]);

  return (
    <div>
      {/* Gastos programados: Mabel 7-oct-2026 «esto no es necesario» aquí arriba → plegados tras un botón
          (siguen avisando cuándo toca pagar la renta, etc.; nada se borró). */}
      {conProgramados && (
        <div className="mb-4">
          <button onClick={() => setVerProgramados((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-content-2 transition hover:bg-surface-2">
            <CalendarClock size={14} /> Gastos fijos programados <ChevronDown size={14} className={`transition ${verProgramados ? 'rotate-180' : ''}`} />
          </button>
          {verProgramados && <div className="mt-3"><RecurringPanel /></div>}
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle Día / Semana / Mes */}
          <div className="inline-flex rounded-lg border border-line bg-surface p-0.5">
            {([['day', 'Día'], ['week', 'Semana'], ['month', 'Mes']] as const).map(([k, label]) => (
              <button key={k} onClick={() => setRange(k)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition ${range === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navRange(-1)}
              className="rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content-2 transition hover:bg-surface-2">←</button>
            <span className="min-w-[9rem] text-center text-sm font-medium text-content">{periodLabel(resp, range)}</span>
            <button onClick={() => navRange(1)}
              className="rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content-2 transition hover:bg-surface-2">→</button>
          </div>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {conCatalogo && <button onClick={() => setShowCatalogo(true)}
              className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
              Categorías y conceptos
            </button>}
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
              <Plus size={16} /> {textoBoton}
            </button>
          </div>
        )}
      </div>

      {showForm && <ExpenseForm onClose={() => setShowForm(false)} onCreated={onCreated} secciones={secciones} titulo={textoBoton} />}
      {showCatalogo && <CatalogoModal onClose={() => { setShowCatalogo(false); reload(); }} />}

      {loading ? (
        <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : !resp || rows.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState icon={Wallet} title={`${vacio || 'Sin gastos'} · ${range === 'day' ? 'este día' : range === 'week' ? 'esta semana' : 'este mes'}`}
            subtitle={isAdmin ? `Usa “${textoBoton}” para capturar el primero.` : 'No hay nada capturado en el periodo.'} />
        </div>
      ) : (
        <>
          {/* Total + desglose por categoría */}
          <div className="mb-4 rounded-2xl border border-line bg-surface p-5 shadow-sm">
            <div className="text-sm text-content-2">{titulo} · {periodLabel(resp, range)}</div>
            <div className="font-display text-3xl font-bold text-red-600 dark:text-red-400"><CountUp value={total} format={money} /></div>
            <div className="text-xs text-content-3">{rows.length} registro{rows.length === 1 ? '' : 's'}
              {fijos && <> · Fijos <span className="font-semibold text-content-2">{money(fijos.f)}</span> · Variables <span className="font-semibold text-content-2">{money(fijos.v)}</span></>}
            </div>

            <div className="mt-4 space-y-3">
              {porCategoria.map((c) => (
                <div key={c.nombre}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="truncate font-medium text-content">{c.nombre}</span>
                    <span className="tabular-nums text-content">{money(c.total)} <span className="text-content-3">({c.n})</span></span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-surface-2">
                    <div className="h-full rounded-full bg-red-400/80" style={{ width: `${(c.total / catMax) * 100}%` }} />
                  </div>
                  {c.conceptos.size > 0 && (
                    <div className="mt-1 space-y-0.5 pl-3">
                      {[...c.conceptos.entries()].sort((a, b) => b[1].total - a[1].total).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between text-xs text-content-3">
                          <span className="truncate">{k}</span><span className="tabular-nums">{money(v.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Lista de gastos */}
          <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
            <table className="w-full text-sm">
              <tbody>
                {rows.map((x) => (
                  <tr key={x.expense_id} className="group border-t border-line first:border-0 transition even:bg-surface-2/40 hover:bg-surface-2">
                    <td className="w-16 px-4 py-2.5 text-content-3">{fmtDay(x.datetime)}</td>
                    <td className="px-4 py-2.5 font-medium text-content">{x.concept_name ? `${x.concept_category} · ${x.concept_name}` : (x.category || 'Sin categoría')}
                      {Number(x.is_fixed) === 1 && <span className="ml-2 rounded-full bg-sky-500/15 px-2 py-0.5 text-[11px] font-semibold text-sky-700 dark:text-sky-300">Fijo</span>}
                    </td>
                    <td className="px-4 py-2.5 text-content-2">{x.notes || ''}{x.iva_amount ? <span className="ml-1 text-xs text-content-3">· IVA {money(x.iva_amount)}</span> : null}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-red-600 dark:text-red-400">−{money(x.amount)}</td>
                    <td className="w-8 px-2 py-2.5 text-right">
                      {isAdmin && x.kydemy_id == null && (
                        <button onClick={() => onDelete(x.expense_id)} title="Eliminar gasto"
                          className="hover-reveal text-content-3 opacity-0 transition hover:text-red-500 group-hover:opacity-100">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
